/**
 * The order of the three things that happen when someone opens the catalogue.
 *
 * The assistant's portrait appears, she delivers the spoken welcome, and only
 * when she has finished speaking does she step aside and the chat panel open
 * in her place. One introduction in three beats, rather than a picture, a
 * voice and a dialog all arriving at once and competing.
 *
 * This module carries the signal that joins them. The portrait *claims* the
 * introduction as it mounts and *releases* it when the recording ends; the
 * panel asks to be told when the introduction is over, and opens then.
 *
 * Claiming is separate from releasing for two reasons, both of which were
 * bugs before they were reasons:
 *
 *   * **A page with no portrait must not be made to wait.** The public
 *     course page carries the panel but no welcome, so nothing claims, and
 *     the panel opens at once rather than sitting out a timeout.
 *   * **The welcome can be given again.** A visitor returning to the
 *     catalogue hears it again, by request, and a one-way "already done"
 *     flag would have let the panel open over the top of the second one.
 *
 * Every ending counts as a release — the recording finishing, failing, being
 * stopped, or never existing — because the panel is the assistant's actual
 * usefulness and must not be contingent on audio working. And the wait is
 * capped: browsers will not play audio at a page nobody has touched, so a
 * visitor who never clicks would otherwise leave the portrait standing there
 * indefinitely.
 *
 * Module state rather than context: the panel lives in the layout and the
 * portrait on the page inside it, so there is no shared ancestor to hang a
 * provider on, and this carries nothing worth re-rendering for.
 */

/**
 * Longest the panel will wait for a welcome that never finishes.
 *
 * Comfortably past the length of the recording. It is a backstop for the
 * visitor who never clicks — not a guess at how long the greeting runs, which
 * is measured rather than assumed.
 */
const CAP_MS = 45_000;

/**
 * Which claim is in force, or 0 for none.
 *
 * A number rather than a flag because a claim has to be *identifiable*. React
 * tears an effect down and mounts it again in development, and the abandoned
 * run's asynchronous work carries on for a while afterwards — long enough to
 * release a claim that by then belongs to its replacement, which let the
 * panel open over a portrait that had not said a word. Holding the token it
 * was given makes a stale release recognisable, and ignorable.
 */
let claim = 0;
let nextClaim = 0;
/** A release that has been scheduled but not yet acted on. */
let release = 0;
const waiting = new Set<() => void>();

/**
 * The welcome is starting: hold the panel back until it is done.
 *
 * Called as the portrait mounts, before it knows whether there is a recording
 * to play — releasing early is cheap, and claiming late would let the panel
 * slip out in front of her.
 */
export function beginWelcome(): number {
  // A release scheduled a moment ago belongs to a mount that is going away;
  // this is its replacement arriving, and the claim carries over.
  if (release) {
    window.clearTimeout(release);
    release = 0;
  }
  nextClaim += 1;
  claim = nextClaim;
  // The chat — panel and button alike — is hidden while she is on stage (see
  // body[data-sx-welcoming] in styles.css), including a panel that was
  // already open when the visitor came back to the catalogue. Hidden, not
  // closed: a conversation in it survives.
  document.body.dataset.sxWelcoming = 'on';
  return claim;
}

/** Told every time a welcome ends, not only the first. */
const endListeners = new Set<() => void>();

/**
 * Be told whenever a welcome finishes.
 *
 * `whenWelcomeOver` answers once, for a panel that is mounting. A panel that
 * stays mounted while the visitor leaves the catalogue and comes back — the
 * layout outlives the page — needs to hear about the next welcome as well, so
 * it can open as she steps aside.
 */
export function onEveryWelcomeEnd(listener: () => void): () => void {
  endListeners.add(listener);
  return () => {
    endListeners.delete(listener);
  };
}

/**
 * The welcome is over, however it ended.
 *
 * Takes the token `beginWelcome` returned, and does nothing unless that claim
 * is still the one in force.
 *
 * The release is also scheduled rather than immediate, for the same
 * development re-mount: the teardown releases a claim the re-mount is about
 * to make again, and acting on it straight away opened the panel over the top
 * of the portrait. A release that is immediately re-claimed never happens;
 * one that is not takes effect on the next tick, which no caller can tell
 * from at once.
 */
export function welcomeOver(token: number): void {
  // Not the claim in force: an abandoned run finishing late. It has nothing
  // left to release.
  if (token !== claim || release) return;
  release = window.setTimeout(() => {
    release = 0;
    claim = 0;
    delete document.body.dataset.sxWelcoming;
    for (const resume of waiting) resume();
    waiting.clear();
    for (const listener of endListeners) listener();
  }, 0);
}

/**
 * Resolves once the welcome is over, or immediately if none is being given.
 *
 * The returned canceller stops the caller being resumed at all, for a
 * component that goes away while waiting.
 */
export function whenWelcomeOver(resume: () => void): () => void {
  if (claim === 0) {
    resume();
    return () => undefined;
  }

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    window.clearTimeout(timer);
    waiting.delete(finish);
    resume();
  };
  const timer = window.setTimeout(finish, CAP_MS);
  waiting.add(finish);

  return () => {
    done = true;
    window.clearTimeout(timer);
    waiting.delete(finish);
  };
}
